/**
 * Extrait un message d'erreur AFFICHABLE depuis une réponse API (axios).
 *
 * Garde-fou critique : un 422 FastAPI renvoie ``detail`` sous forme de TABLEAU
 * d'objets (``[{loc, msg, type}, …]``). Le passer tel quel à ``toast.error`` ou
 * le rendre dans du JSX (``{error}``) lève « Objects are not valid as a React
 * child » → écran blanc. On réduit donc TOUJOURS en string avant affichage.
 *
 * Enrichi (audit tunnel association, 2026-07-09 — une vraie asso a vu « Network
 * Error » brut) :
 *  - PANNE RÉSEAU / TIMEOUT (pas de ``response``) → message FR actionnable, plus
 *    jamais le ``e.message`` axios anglais ni un fallback mensonger (« Code
 *    invalide » alors que la requête n'est jamais partie) ;
 *  - 429 → « Trop de tentatives » ; 502+ → « service momentanément indisponible » ;
 *  - FILTRE ANTI-TECHNIQUE : un ``detail`` interne (« Stripe X: Request req_… »,
 *    traceback…) n'est JAMAIS montré à l'utilisateur → fallback (le technique
 *    reste dans les logs serveur/Sentry).
 *
 * @param {any} err - l'erreur axios (ou autre)
 * @param {string} fallback - message par défaut si rien d'exploitable
 * @returns {string}
 */

// Détail technique interne qui ne doit JAMAIS atteindre un écran utilisateur.
const TECHNICAL_DETAIL = /^Stripe\s[\w.]+:|req_[A-Za-z0-9]{6,}|Traceback|Internal Server Error/i;

export function apiErrMsg(err, fallback = "Une erreur est survenue") {
  // 1) Pas de réponse serveur = panne réseau ou timeout → message actionnable.
  if (err && err.request !== undefined && !err.response) {
    if (err.code === "ECONNABORTED" || /timeout/i.test(String(err.message || ""))) {
      return "Le serveur met trop de temps à répondre. Réessayez dans un instant.";
    }
    return "Connexion impossible. Vérifiez votre connexion Internet et réessayez.";
  }
  const status = err?.response?.status;
  const d = err?.response?.data?.detail;
  const clean = (s) => (TECHNICAL_DETAIL.test(s) ? null : s);
  if (typeof d === "string" && d) {
    const c = clean(d);
    if (c) return c;
  }
  if (Array.isArray(d)) {
    const msg = d
      .map((x) => (typeof x === "string" ? x : x?.msg || x?.message || ""))
      .filter(Boolean)
      .join(", ");
    if (msg && clean(msg)) return msg;
  }
  if (d && typeof d === "object" && !Array.isArray(d)) {
    const m = d.msg || d.message;
    if (m && clean(String(m))) return m;
  }
  // 2) Statuts fréquents sans detail exploitable → messages FR standard.
  // 451 « Unavailable For Legal Reasons » — les conditions ont changé et le
  // serveur refuse d'agir tant qu'elles ne sont pas ré-acceptées (garde
  // ``dependencies.require_legal_acceptance_any``, 05/09/2026). Son ``detail``
  // est un OBJET : sans ce cas, on tombait dans le fallback de l'appelant, qui
  // accuse au choix les champs du formulaire ou la connexion Internet — deux
  // messages FAUX qui envoient l'utilisateur chercher un problème inexistant.
  // La modale d'acceptation se monte par-dessus ; ce texte n'est que le filet.
  if (status === 451) {
    return "Nos conditions ont été mises à jour. Acceptez-les pour continuer.";
  }
  if (status === 429) return "Trop de tentatives. Patientez quelques minutes puis réessayez.";
  if (status >= 502) return "Le service est momentanément indisponible. Réessayez dans quelques minutes.";
  return fallback;
}
